import { Button } from '@/components/ui/button';
import { ChevronDown, X, Compass } from 'lucide-react';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import engagementMethodsData from '@/data/scripture-engagement-methods.json';
import { CapabilityContextModal } from './capability-context-modal';
import { useManuscript } from '@/context/manuscript-context';
import topoBackground from '../../assets/expedition-topo-bg.png';
import { GuidedStudyLoadingSkeleton } from './guided-study/GuidedStudyLoadingSkeleton';
import { GuidedStudyEmptyState } from './guided-study/GuidedStudyEmptyState';
import { GuidedStudyNavigation } from './guided-study/GuidedStudyNavigation';
import { GuidedStudyPersonaSelector } from './guided-study/GuidedStudyPersonaSelector';
import { GuidedStudyProgressBar } from './guided-study/GuidedStudyProgressBar';
import { GuidedStudyStepContent } from './guided-study/GuidedStudyStepContent';
import { useGuidedStudyCapabilityHandler } from './guided-study/use-guided-study-capability-handler';
import { useGuidedStudyProgress } from '@/lib/hooks/use-guided-study-progress';
import { useGuidedStudyPersona } from './guided-study/hooks/use-guided-study-persona';
import { useScrollIndicators } from '@/lib/hooks/use-scroll-indicators';

interface Props {
  passageId: string;
  bookName?: string;
  onClose: () => void;
}

export function GuidedStudySubheader({
  passageId,
  bookName,
  onClose,
}: Props) {
  const isDarkMode = useDarkMode();

  // Custom hooks for logic extraction
  const {
    guidedStudy,
    allStudies,
    isLoading: isPersonaLoading,
    switchPersona
  } = useGuidedStudyPersona(passageId);

  const {
    scrollRef,
    canScrollUp,
    canScrollDown,
    checkScroll
  } = useScrollIndicators();

  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [hoverInfo, setHoverInfo] = useState<{ x: number; title: string; stepNum: number; isReached: boolean } | null>(null);
  const [showHint, setShowHint] = useState(false);

  // Get progress for the current passage and persona
  const {
    currentStepIndex,
    currentSubStepIndex,
    totalSubSteps,
    isCompleted,
    isLoading: isProgressLoading,
    goToNext,
    goToPrevious,
    markComplete,
    saveTextResponse,
    getTextResponse,
    markRequirementMet,
    isRequirementMet,
    isFirstStep,
    isLastStep,
    maxReachedStepIndex,
    maxReachedSubStepIndex,
    jumpTo
  } = useGuidedStudyProgress(
    passageId,
    guidedStudy?.personaId,
    guidedStudy
  );

  // Combine loading states to prevent flash
  const isLoading = isPersonaLoading || isProgressLoading;

  // Use the capability handler hook
  const {
    showCapabilityModal,
    setShowCapabilityModal,
    showFollowupPrompt,
    setShowFollowupPrompt,
    hasViewedCapability,
    setHasViewedCapability,
    activeCapabilityRef,
    activeCapabilityItems,
    handleCapabilityAction,
  } = useGuidedStudyCapabilityHandler({
    passageId,
    currentStep: guidedStudy?.steps?.[currentStepIndex],
    currentStepIndex,
    currentSubStepIndex,
    totalSubSteps,
    markRequirementMet,
  });

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const {
    marks,
    connections,
    dividers,
    loading: manuscriptLoading,
    drawModeStyleId,
    setManuallyOpened,
    setActiveTab,
    setDrawModeStyleId,
    setTransientHighlights,
    setInteractionConstraint,
  } = useManuscript();

  const currentStep = guidedStudy?.steps?.[currentStepIndex];

  const instruction = (currentStep?.instruction || '').toLowerCase();
  const isManuscriptStep = currentStep?.capabilityRef?.startsWith('manuscript-study') ||
    instruction.includes('use the marking tools');
  const totalSteps = guidedStudy?.steps?.length || 0;

  // Get the current text response for this step/substep
  const currentTextResponse = getTextResponse(currentStepIndex, currentSubStepIndex);
  const [userInput, setUserInput] = useState(currentTextResponse);
  const [finalReflection, setFinalReflection] = useState(getTextResponse(999, 999));

  // Debounce timer for auto-saving text responses
  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Helper to get substep count for any step
  const getSubStepCount = (step: GuidedStudyStep) => {
    if (!step) return 1;
    if (step.questions && step.questions.length > 0) return step.questions.length;
    if (step.capabilityItems && step.capabilityItems.length > 0) return step.capabilityItems.length;
    if (step.steps && step.steps.length > 0) return step.steps.length;

    // Fallback: Check for methodRef steps in metadata
    if (step.methodRef) {
      for (const category of engagementMethodsData.categories) {
        const method = category.methods.find((m: any) => m.id === step.methodRef);
        if (method && method.steps && method.steps.length > 0) {
          return method.steps.length;
        }
      }
    }

    return 1;
  };

  // Calculate total progress across all steps and sub-steps
  let completedSubSteps = 0;
  let totalSubStepsInStudy = 0;

  if (guidedStudy?.steps) {
    for (let i = 0; i < currentStepIndex; i++) {
      const step = guidedStudy.steps[i];
      if (step) {
        completedSubSteps += getSubStepCount(step);
      }
    }
    completedSubSteps += currentSubStepIndex;

    for (const step of guidedStudy.steps) {
      totalSubStepsInStudy += getSubStepCount(step);
    }
  }

  const progressPercent = totalSubStepsInStudy > 0 ? (completedSubSteps / totalSubStepsInStudy) * 100 : 0;

  // Calculate max reached progress for visual indicator
  let maxCompletedSubSteps = 0;
  if (guidedStudy?.steps) {
    for (let i = 0; i < maxReachedStepIndex; i++) {
      const step = guidedStudy.steps[i];
      if (step) {
        maxCompletedSubSteps += getSubStepCount(step);
      }
    }
  }
  maxCompletedSubSteps += maxReachedSubStepIndex;
  const maxProgressPercent = totalSubStepsInStudy > 0 ? (maxCompletedSubSteps / totalSubStepsInStudy) * 100 : 0;

  // Reset followup prompt, hint, and capability view state when step changes
  // Also load the saved text response for the new step
  useEffect(() => {
    // Initialize based on saved requirement status
    if (isRequirementMet && isRequirementMet(currentStepIndex, currentSubStepIndex)) {
      setHasViewedCapability(true);
    } else {
      setHasViewedCapability(false);
    }

    // Load saved text response for this step/substep
    const savedResponse = getTextResponse(currentStepIndex, currentSubStepIndex);
    setUserInput(savedResponse);
    setFinalReflection(getTextResponse(999, 999));

    // Clear previous ephemeral state when step changes
    if (currentStep) {
      setTransientHighlights([]);
      setInteractionConstraint(null);

      // Close manuscript tools and reset draw mode when navigating away from manuscript steps
      const stepIsManuscript = currentStep.capabilityRef?.startsWith('manuscript-study') ||
        (currentStep.instruction || '').toLowerCase().includes('use the marking tools');

      if (!stepIsManuscript) {
        setManuallyOpened(false);
        setDrawModeStyleId(null); // Only reset draw mode when leaving manuscript steps
      }
    }

    // Reset scroll to top when step changes
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }

    // 4. Apply Range Constraints (if present on any step type)
    if (currentStep && currentStep.focusRange) {
      // Try to parse range w-1-1-1-1 format if we can, 
      // but usually the AI gives verse-refs. 
      // For now, focusRange is a hint, but we'll need a way to map verse-refs to word-ids 
      // if we want to truly lock it down. Skipping strict lock for ref-strings for a moment.
    }

    // Clear any pending save timer when step changes
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [currentStepIndex, currentSubStepIndex, isCompleted, getTextResponse, isRequirementMet, currentStep, setActiveTab, setDrawModeStyleId, setTransientHighlights, setInteractionConstraint, setManuallyOpened, setHasViewedCapability]);



  // Re-check scroll when step content changes (transitions)
  useEffect(() => {
    const timer = setTimeout(checkScroll, 150);
    return () => clearTimeout(timer);
  }, [currentStepIndex, currentSubStepIndex, isCompleted, showFollowupPrompt]);

  const handleComplete = async () => {
    await markComplete();
  };

  const handleNext = () => {
    setDirection('forward');
    goToNext();
  };

  const handlePrevious = () => {
    setDirection('backward');
    goToPrevious();
  };

  // Look up engagement method info if methodRef is set
  const getMethodInfo = (methodRef: string, methodStepRef?: string) => {
    for (const category of engagementMethodsData.categories) {
      const method = category.methods.find((m: { id: string }) => m.id === methodRef);
      if (method) {
        const step = methodStepRef
          ? method.steps?.find((s: { id: string }) => s.id === methodStepRef)
          : null;
        return {
          methodTitle: method.title,
          methodSummary: method.summary,
          stepTitle: step?.title,
          stepDescription: step?.description
        };
      }
    }
    return null;
  };

  const methodInfo = (currentStep && currentStep.methodRef) ? getMethodInfo(currentStep.methodRef, currentStep.methodStepRef) : null;

  // Get the effective steps for the current step (explicit or fallback from methodRef)
  const getEffectiveMethodSteps = () => {
    if (currentStep.steps && currentStep.steps.length > 0) {
      return currentStep.steps;
    }

    if (currentStep.methodRef) {
      for (const category of engagementMethodsData.categories) {
        const method = category.methods.find((m: any) => m.id === currentStep.methodRef);
        if (method && method.steps && method.steps.length > 0) {
          return method.steps.map(s => ({
            id: s.id,
            instruction: s.description,
            title: s.title,
            capabilityRef: s.capabilityRef
          }));
        }
      }
    }
    return null;
  };

  const effectiveMethodSteps = currentStep ? getEffectiveMethodSteps() : null;

  // Get the current sub-step content
  const getCurrentSubStepContent = () => {
    // Guard against undefined currentStep
    if (!currentStep) {
      return { type: 'step' as const, content: null };
    }

    if (currentStep.questions && currentStep.questions.length > 0) {
      return {
        type: 'question' as const,
        content: currentStep.questions[currentSubStepIndex]
      };
    }

    if (currentStep.capabilityItems && currentStep.capabilityItems.length > 0) {
      return {
        type: 'capabilityItem' as const,
        content: currentStep.capabilityItems[currentSubStepIndex]
      };
    }

    if (effectiveMethodSteps) {
      return {
        type: 'methodStep' as const,
        content: effectiveMethodSteps[currentSubStepIndex]
      };
    }

    // No sub-steps, show the whole step
    return {
      type: 'step' as const,
      content: null
    };
  };

  const subStepContent = getCurrentSubStepContent();

  // Check if this sub-step requires viewing capability data before proceeding
  const requiresCapabilityView = subStepContent.type === 'capabilityItem' && currentStep?.capabilityRef;

  // For manuscript steps, we require at least one marking (unless optional)
  // ENFORCEMENT: If this is a manuscript step, they MUST perform an action
  // unless the instruction expressly says it's optional
  const isOptional = instruction.includes('optional') || currentStep?.optional;
  const hasMarks = marks.length > 0 || connections.length > 0 || dividers.length > 0;
  const isManuscriptRequirementMet = !isManuscriptStep || isOptional || hasMarks || hasViewedCapability;

  // Persist if they've met the manuscript requirement
  useEffect(() => {
    if (currentStep && isManuscriptStep && hasMarks && !hasViewedCapability) {
      setHasViewedCapability(true);
      markRequirementMet(currentStepIndex, currentSubStepIndex);
    }
  }, [isManuscriptStep, hasMarks, hasViewedCapability, currentStepIndex, currentSubStepIndex, markRequirementMet, currentStep]);

  const canProceed = (!requiresCapabilityView || hasViewedCapability) && isManuscriptRequirementMet && !manuscriptLoading;

  const getProceedDisabledReason = () => {
    if (manuscriptLoading) return "Loading manuscript...";
    if (isManuscriptStep && !isManuscriptRequirementMet) return "Add a mark to the text to proceed";
    if (requiresCapabilityView && !hasViewedCapability) return "View instructions first";
    return null;
  };

  const disabledReason = getProceedDisabledReason();

  // 1. Loading Skeleton
  if (isLoading) {
    return <GuidedStudyLoadingSkeleton isDarkMode={isDarkMode} />;
  }

  // 2. Empty State
  if (!guidedStudy) {
    return (
      <GuidedStudyEmptyState
        isDarkMode={isDarkMode}
        passageId={passageId}
        onClose={onClose}
      />
    );
  }

  // 3. Normal Guided Study Content
  if (!currentStep) {
    if (guidedStudy) {
      console.error('No current step found!', { currentStepIndex, totalSteps, guidedStudy });
    }
    return null;
  }

  return (
    <div
      className={`relative w-full overflow-y-auto transition-all duration-500 border-b scrollbar-hide ${isDarkMode
        ? 'bg-[#0a0a0b]/85 backdrop-blur-md border-amber-900/30'
        : 'bg-[#fdfbf7]/90 backdrop-blur-md border-amber-100'
        }`}
      ref={scrollRef}
      onScroll={checkScroll}
      style={{
        height: 'auto',
        minHeight: '360px',
        maxHeight: '85vh'
      }}
    >
      {/* Expedition Background Visual */}
      <div className={`absolute inset-0 z-0 pointer-events-none overflow-hidden ${isDarkMode ? 'opacity-40' : 'opacity-40'}`}>
        <img
          src={topoBackground}
          alt=""
          className={`w-full h-full object-cover scale-110 transition-opacity duration-1000 ${isDarkMode ? 'brightness-[1.8] saturate-[1.2] mix-blend-screen' : 'brightness-95 saturate-[0.8] mix-blend-multiply'} `}
        />
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-b from-[#0a0a0b] via-transparent to-[#0a0a0b]'
          : 'bg-gradient-to-b from-[#fdfbf7] via-transparent to-[#fdfbf7]'
          } `}
        />
        {/* Subtle Map Texture Overlay */}
        <div className={`absolute inset-0 pointer-events-none ${isDarkMode ? 'opacity-[0.03] invert' : 'opacity-[0.03]'}`} style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/parchment.png")' }} />
      </div>

      {/* Main content area */}
      {/* Main content area */}
      <div className="flex-1 relative flex flex-col w-full">
        {/* Top Header Section (Fixed) */}
        <div className="container mx-auto px-4 pt-4 pb-0 max-w-4xl shrink-0">
          {/* Header Row */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              {/* Persona Switcher Dropdown */}
              <GuidedStudyPersonaSelector
                guidedStudy={guidedStudy}
                allStudies={allStudies}
                onPersonaSwitch={switchPersona}
                isDarkMode={isDarkMode}
              />
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-9 w-9 rounded-full bg-foreground/5 hover:bg-foreground/10 text-muted-foreground/60 hover:text-foreground transition-all shrink-0"
              aria-label="Close Guided Study"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Progress Bar */}
          <GuidedStudyProgressBar
            guidedStudy={guidedStudy}
            currentStep={currentStep}
            currentStepIndex={currentStepIndex}
            currentSubStepIndex={currentSubStepIndex}
            totalSteps={totalSteps}
            totalSubSteps={totalSubSteps}
            totalSubStepsInStudy={totalSubStepsInStudy}
            progressPercent={progressPercent}
            maxProgressPercent={maxProgressPercent}
            isCompleted={isCompleted}
            maxReachedStepIndex={maxReachedStepIndex}
            maxReachedSubStepIndex={maxReachedSubStepIndex}
            isDarkMode={isDarkMode}
            getSubStepCount={getSubStepCount}
            onJumpTo={jumpTo}
            hoverInfo={hoverInfo}
            setHoverInfo={setHoverInfo}
          />
        </div>

        {/* Body area */}
        <div className="flex-1 relative">
          <div className="w-full">
            <div className="container mx-auto px-4 pt-0 pb-6 max-w-4xl">
              <GuidedStudyStepContent
                guidedStudy={guidedStudy}
                currentStep={currentStep}
                currentStepIndex={currentStepIndex}
                currentSubStepIndex={currentSubStepIndex}
                totalSubSteps={totalSubSteps}
                isCompleted={isCompleted}
                direction={direction}
                showFollowupPrompt={showFollowupPrompt}
                showHint={showHint}
                setShowHint={setShowHint}
                hasViewedCapability={hasViewedCapability}
                userInput={userInput}
                setUserInput={setUserInput}
                finalReflection={finalReflection}
                setFinalReflection={setFinalReflection}
                saveTextResponse={saveTextResponse}
                handleCapabilityAction={handleCapabilityAction}
                onClose={onClose}
                isDarkMode={isDarkMode}
                methodInfo={methodInfo}
                subStepContent={subStepContent}
              />
            </div>
          </div>

          {/* Top/Bottom Fades */}
          <div className={`absolute top-0 left-0 right-0 h-8 pointer-events-none z-20 transition-opacity duration-300 ${canScrollUp ? 'opacity-100' : 'opacity-0'} ${isDarkMode ? 'bg-gradient-to-b from-[#0a0a0b] to-transparent' : 'bg-gradient-to-b from-[#fdfbf7] to-transparent'} `} />
          <div className={`absolute bottom-0 left-0 right-0 h-10 pointer-events-none z-20 transition-opacity duration-300 ${canScrollDown ? 'opacity-100' : 'opacity-0'} ${isDarkMode ? 'bg-gradient-to-t from-[#0a0a0b] to-transparent' : 'bg-gradient-to-t from-[#fdfbf7] to-transparent'} `} />

          {/* Floating "More" Indicator */}
          <AnimatePresence>
            {canScrollDown && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="absolute bottom-3 left-1/2 -translate-x-1/2 z-30 pointer-events-none"
              >
                <div
                  className={`flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-md border shadow-lg ${isDarkMode
                    ? 'bg-amber-500/10 border-amber-500/30 text-amber-500'
                    : 'bg-primary/10 border-primary/20 text-primary'
                    } `}
                >
                  <Compass className="h-4 w-4 animate-spin-slow" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] animate-pulse">Consult the Map</span>
                  <ChevronDown className="h-3.5 w-3.5 animate-bounce" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div >

        {/* Sticky Navigation Footer */}
        {!isCompleted && (
          <GuidedStudyNavigation
            isDarkMode={isDarkMode}
            isFirstStep={isFirstStep}
            isLastStep={isLastStep}
            canProceed={canProceed}
            disabledReason={disabledReason}
            currentStepOptional={currentStep.optional}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onComplete={handleComplete}
          />
        )}
      </div>

      {/* Capability Modal Container */}
      <CapabilityContextModal
        isOpen={showCapabilityModal}
        onClose={() => {
          setShowCapabilityModal(false);

          // Show followup prompt if it exists (captured from modal close)
          if (currentStep.followupPrompt && currentSubStepIndex === totalSubSteps - 1) {
            setShowFollowupPrompt(true);
          }

          // Auto-advance for capability items to reduce friction between sequential items
          if (subStepContent.type === 'capabilityItem' && currentSubStepIndex < totalSubSteps - 1) {
            handleNext();
          }
        }}
        capabilityRef={activeCapabilityRef || currentStep.capabilityRef || ''}
        capabilityItems={activeCapabilityItems || (subStepContent.type === 'capabilityItem' ? [currentStep.capabilityItems![currentSubStepIndex]] : (currentStep.capabilityItems || []))}
        passageId={passageId}
        bookName={bookName}
      />
    </div >
  );
}
